import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileAudio, FileVideo, Sparkles, ClipboardCheck, ChevronRight } from "lucide-react";

export default function StudentDebriefs() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { if (!loading && !user) navigate("/", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => {
    if (!user) return;
    api.get("/student/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="debriefs-page">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Lesson recaps</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Your debriefs</h1>
      <p className="mt-2 text-slate-600 max-w-2xl">
        After your instructor delivers a module live, they upload the recording and publish a structured recap
        with key takeaways and next steps.
      </p>

      <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-hidden">
        {sessions.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            <ClipboardCheck className="w-8 h-8 mx-auto mb-3 text-slate-400" />
            No recaps published yet. Once your instructor publishes one, it&apos;ll appear here.
          </div>
        )}
        {sessions.map((s) => (
          <button
            key={s.session_id}
            onClick={() => setOpen(s)}
            className="w-full text-left px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors grid grid-cols-12 gap-4 items-center"
            data-testid={`debrief-${s.session_id}`}
          >
            <div className="col-span-12 md:col-span-3">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Date</div>
              <div className="font-mono text-sm text-slate-900 mt-0.5">{s.flight_date}</div>
              <div className="font-mono text-xs text-slate-500 mt-0.5">{s.duration_minutes} min</div>
            </div>
            <div className="col-span-12 md:col-span-6">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Module</div>
              <div className="font-heading font-semibold text-slate-900 text-base mt-0.5">{s.lesson_title}</div>
              <div className="text-xs text-slate-500 mt-0.5">Instructor: {s.instructor_name}</div>
            </div>
            <div className="col-span-12 md:col-span-3 md:text-right flex md:justify-end items-center gap-1 text-[var(--brand-primary)] text-sm font-semibold">
              <Sparkles className="w-4 h-4" /> View recap <ChevronRight className="w-4 h-4" />
            </div>
          </button>
        ))}
      </div>

      {open && <DebriefViewer session={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function DebriefViewer({ session, onClose }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 rounded-sm max-w-4xl max-h-[90vh] overflow-auto" data-testid="debrief-viewer">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            {session.lesson_title}
            <span className="text-slate-500 font-normal text-sm ml-2">· {session.flight_date}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-sm mt-2">
          <Meta label="Instructor" value={session.instructor_name} />
          <Meta label="Date" value={session.flight_date} />
          <Meta label="Duration" value={`${session.duration_minutes} min`} />
        </div>

        {session.media_storage_path && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 mt-2">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[var(--brand-primary)] flex items-center gap-2">
              {session.media_type === "video" ? <FileVideo className="w-4 h-4" /> : <FileAudio className="w-4 h-4" />}
              Live lesson recording
            </div>
            {session.media_type === "video"
              ? <video controls src={fileUrl(session.media_storage_path)} className="mt-3 w-full max-h-72 bg-black rounded-sm" />
              : <audio controls src={fileUrl(session.media_storage_path)} className="mt-3 w-full" />}
          </div>
        )}

        <Section title="Lesson recap" tone="brand">
          <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{session.ai_summary}</p>
        </Section>
        {session.instructor_notes && (
          <Section title="Instructor notes" tone="info">
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{session.instructor_notes}</p>
          </Section>
        )}
        <Section title="Key takeaways" tone="success">
          <Bullets items={session.ai_done_well} />
        </Section>
        <Section title="Watch out for" tone="error">
          <Bullets items={session.ai_improvements} />
        </Section>
        <Section title="Next steps" tone="brand">
          <Bullets items={session.ai_action_items} />
        </Section>
        {session.ai_transcript && (
          <details className="bg-slate-50 border border-slate-200 rounded-sm p-4">
            <summary className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 cursor-pointer">Verbatim transcript</summary>
            <div className="mt-3 text-slate-700 text-sm leading-relaxed whitespace-pre-wrap font-mono">{session.ai_transcript}</div>
          </details>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
          <Link to={`/report/${session.lesson_id}`} className="text-[var(--brand-primary)] hover:underline text-sm font-medium">
            View module report →
          </Link>
          <Button onClick={onClose} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className="font-mono text-slate-900 text-sm mt-0.5">{value}</div>
    </div>
  );
}

function Section({ title, tone, children }) {
  const toneClass = {
    brand: "border-[var(--brand-primary)]/30 bg-[var(--brand-light)]/40",
    success: "border-emerald-200 bg-emerald-50/50",
    error: "border-red-200 bg-red-50/50",
    info: "border-blue-200 bg-blue-50/50",
  }[tone] || "border-slate-200 bg-slate-50";
  const labelTone = {
    brand: "text-[var(--brand-primary)]",
    success: "text-emerald-700",
    error: "text-red-700",
    info: "text-blue-700",
  }[tone] || "text-slate-700";
  return (
    <div className={`border rounded-sm p-4 ${toneClass}`}>
      <div className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${labelTone}`}>{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
function Bullets({ items }) {
  if (!items?.length) return <div className="text-slate-500 text-sm italic">(none)</div>;
  return (
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="text-slate-700 text-sm flex gap-2 leading-relaxed">
          <span className="text-[var(--brand-primary)] shrink-0">•</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
